import React from 'react';
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from 'react-router-dom';
import { Trophy, Medal, Award } from 'lucide-react';
import { toast } from 'sonner';

// Compact share buttons for podium
const ShareButtonsCompact = ({ gameId, userId, gameName, accuracy, place, t }) => {
  const shareUrl = `https://countryscanner.ru/game/${gameId}/user-results/${userId}`;
  const placeText = place === 1 ? '1-е место 🥇' : place === 2 ? '2-е место 🥈' : '3-е место 🥉';
  const shareText = `Я занял ${placeText} в игре "${gameName}" с точностью ${accuracy}%! Попробуй побить мой рекорд в Country Scanner!`;

  const handleShare = (platform, e) => {
    e.stopPropagation();
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(shareText);
    
    switch (platform) {
      case 'telegram':
        window.open(`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodedText}%0A${encodedUrl}`, '_blank');
        break;
      case 'vk':
        window.open(`https://vk.com/share.php?url=${encodedUrl}&title=${encodedText}`, '_blank');
        break;
      case 'ok':
        window.open(`https://connect.ok.ru/offer?url=${encodedUrl}&title=${encodedText}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl).then(() => {
          toast.success(t ? t('share.linkCopied') : 'Link copied!');
        }).catch(() => {
          toast.error(t ? t('share.copyFailed') : 'Copy failed');
        });
        break;
    }
  };

  const btnStyle = {
    padding: '6px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.15s'
  };

  return (
    <div style={{ 
      display: 'flex', 
      gap: '6px', 
      justifyContent: 'center',
      marginTop: '0.75rem',
      paddingTop: '0.75rem',
      borderTop: '1px solid #e2e8f0'
    }}>
      <span style={{ fontSize: '0.8rem', color: '#718096', marginRight: '4px' }}>{t('share.share')}:</span>
      <button onClick={(e) => handleShare('telegram', e)} style={{ ...btnStyle, background: '#0088cc' }} title="Telegram">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
        </svg>
      </button>
      <button onClick={(e) => handleShare('whatsapp', e)} style={{ ...btnStyle, background: '#25D366' }} title="WhatsApp">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </button>
      <button onClick={(e) => handleShare('vk', e)} style={{ ...btnStyle, background: '#0077FF' }} title="VK">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
          <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0zm3.692 17.123h-1.744c-.66 0-.864-.525-2.05-1.727-1.033-1-1.49-1.135-1.744-1.135-.356 0-.458.102-.458.593v1.575c0 .424-.135.678-1.253.678-1.846 0-3.896-1.118-5.335-3.202C4.624 10.857 4 8.49 4 8.08c0-.254.102-.491.593-.491h1.744c.44 0 .61.203.78.678.847 2.455 2.27 4.607 2.86 4.607.22 0 .322-.102.322-.66V9.721c-.068-1.186-.695-1.287-.695-1.71 0-.203.17-.407.44-.407h2.744c.372 0 .508.203.508.643v3.473c0 .372.17.508.271.508.22 0 .407-.136.813-.542 1.253-1.406 2.15-3.574 2.15-3.574.119-.254.322-.491.763-.491h1.744c.525 0 .644.27.525.643-.22 1.017-2.354 4.031-2.354 4.031-.186.305-.254.44 0 .78.186.254.796.779 1.203 1.253.745.847 1.32 1.558 1.473 2.05.17.49-.085.744-.576.744z"/>
        </svg>
      </button>
      <button onClick={(e) => handleShare('ok', e)} style={{ ...btnStyle, background: '#EE8208' }} title="OK">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
          <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm0 4.8a4.2 4.2 0 1 1 0 8.4 4.2 4.2 0 0 1 0-8.4zm0 6.6a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8zm4.789 2.29a7.2 7.2 0 0 1-9.578 0 .9.9 0 0 0-1.211 1.33c.9.82 1.953 1.448 3.1 1.856l-2.648 2.647a.9.9 0 1 0 1.273 1.273L12 16.522l4.275 4.274a.9.9 0 0 0 1.273-1.273l-2.648-2.647a8.085 8.085 0 0 0 3.1-1.856.9.9 0 0 0-1.211-1.33z"/>
        </svg>
      </button>
      <button onClick={(e) => handleShare('copy', e)} style={{ ...btnStyle, background: 'linear-gradient(135deg, #7B68EE, #9370DB)' }} title={t('winners.maxCopy')}>
        <svg width="16" height="16" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" fill="white"/>
          <text x="12" y="16" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#7B68EE" fontFamily="Arial">M</text>
        </svg>
      </button>
    </div>
  );
};

const WinnersPodium = ({ winners, totalPanoramas, gameId, game, currentUserId }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // Get winner config with defaults
  const winnerConfig = game?.winner_config || {};
  const firstThreshold = winnerConfig.first_place_threshold || 95;
  const secondThreshold = winnerConfig.second_place_threshold || 85;
  const thirdThreshold = winnerConfig.third_place_threshold || 75;
  
  // Helper function to format date/time
  const formatCascadeDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Create array of 3 places with null for unfilled positions
  const podium = [1, 2, 3].map(place => {
    const winner = winners.find(w => w.place === place);
    if (winner) {
      const threshold = place === 1 ? firstThreshold : place === 2 ? secondThreshold : thirdThreshold;
      
      // Generate cascade note if needed
      let cascadeNote = null;
      if (winner.threshold_reached && winner.threshold_reached < winner.place) {
        const originalPlaceText = winner.threshold_reached === 1 ? '1-е' : winner.threshold_reached === 2 ? '2-е' : '3-е';
        if (winner.blocked_by_user && winner.blocked_by_time) {
          cascadeNote = `Достигнут порог ${originalPlaceText} места, но оно было занято игроком ${winner.blocked_by_user} (${formatCascadeDate(winner.blocked_by_time)})`;
        } else {
          cascadeNote = `Достигнут порог ${originalPlaceText} места, но оно уже было занято`;
        }
      }
      
      return { ...winner, threshold, cascadeNote };
    }
    return { 
      place, 
      empty: true,
      threshold: place === 1 ? firstThreshold : place === 2 ? secondThreshold : thirdThreshold
    };
  });

  const allWinnersDetermined = winners.length === 3;

  // Format date/time to "01.04.2025 14:30"
  const formatDateTime = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    } catch (e) {
      return null;
    }
  };

  // Medal icons
  const getMedalIcon = (place) => {
    if (place === 1) return <span style={{ fontSize: '2rem' }}>🥇</span>;
    if (place === 2) return <span style={{ fontSize: '2rem' }}>🥈</span>;
    if (place === 3) return <span style={{ fontSize: '2rem' }}>🥉</span>;
    return null;
  };

  // Medal colors
  const getMedalColor = (place) => {
    if (place === 1) return '#FFD700'; // Gold
    if (place === 2) return '#C0C0C0'; // Silver
    if (place === 3) return '#CD7F32'; // Bronze
    return '#ccc';
  };

  const getPlaceName = (place) => {
    if (place === 1) return t('game.firstPlace');
    if (place === 2) return t('game.secondPlace');
    if (place === 3) return t('game.thirdPlace');
    return '';
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      padding: '2rem',
      borderRadius: '12px',
      marginBottom: '2rem'
    }}>
      {/* Status Message */}
      <div style={{
        textAlign: 'center',
        marginBottom: '1.5rem'
      }}>
        {allWinnersDetermined ? (
          <div>
            <h3 style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#2d3748',
              marginBottom: '0.5rem'
            }}>
              <Trophy size={28} style={{ display: 'inline', marginRight: '0.5rem', marginBottom: '0.25rem', color: '#FFD700' }} />
              {t('winners.winnersBeenDetermined')}
            </h3>
            <p style={{
              margin: 0,
              fontSize: '1rem',
              color: '#4a5568'
            }}>
              {t('winners.canPlayCompareAnswers')}
            </p>
          </div>
        ) : (
          <div>
            <h3 style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: 700,
              color: '#2d3748',
              marginBottom: '0.5rem'
            }}>
              <Trophy size={28} style={{ display: 'inline', marginRight: '0.5rem', marginBottom: '0.25rem', color: '#667eea' }} />
              {t("game.gameActive").replace(".", "")}
            </h3>
            <p style={{
              margin: 0,
              fontSize: '1rem',
              color: '#4a5568'
            }}>
              {t("game.canWinPrize").replace(".", "")}
            </p>
          </div>
        )}
      </div>

      {/* Podium - Horizontal List */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-around',
        gap: '1rem',
        flexWrap: 'wrap'
      }}>
        {podium.map((position) => (
          <div
            key={position.place}
            onClick={() => {
              if (!position.empty && gameId) {
                navigate(`/${gameId}/user-results/${position.user_id}`);
              }
            }}
            style={{
              flex: '1',
              minWidth: '250px',
              maxWidth: '350px',
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              border: position.empty ? '2px dashed #cbd5e0' : `3px solid ${getMedalColor(position.place)}`,
              opacity: position.empty ? 0.6 : 1,
              cursor: position.empty ? 'default' : 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!position.empty) {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (!position.empty) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            {/* Medal Icon and Place Name */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1rem',
              paddingBottom: '0.75rem',
              borderBottom: `2px solid ${getMedalColor(position.place)}`
            }}>
              {getMedalIcon(position.place)}
              <h4 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: 700,
                color: '#2d3748'
              }}>
                {getPlaceName(position.place)}
              </h4>
            </div>

            {/* Winner Info or "Not Determined" */}
            {position.empty ? (
              <div style={{
                textAlign: 'center',
                padding: '1.5rem 1rem'
              }}>
                <div style={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: '#718096',
                  marginBottom: '1rem'
                }}>
                  {t("game.placeNotTaken")}
                </div>
                <div style={{
                  padding: '1rem',
                  background: '#f7fafc',
                  borderRadius: '8px',
                  border: '2px dashed #cbd5e0'
                }}>
                  <div style={{
                    fontSize: '0.85rem',
                    color: '#718096',
                    marginBottom: '0.5rem',
                    fontWeight: 600
                  }}>
                    {t("game.requiredAccuracy")}
                  </div>
                  <div style={{
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: '#667eea'
                  }}>
                    ≥ {position.threshold}%
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    color: '#718096',
                    marginTop: '0.5rem',
                    fontStyle: 'italic'
                  }}>
                    {t('game.reachAccuracyToWin')}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                {/* Username */}
                <div style={{
                  marginBottom: '1rem'
                }}>
                  <div style={{
                    fontSize: '0.85rem',
                    color: '#718096',
                    marginBottom: '0.25rem',
                    fontWeight: 600
                  }}>
                    {t('winners.player')}
                  </div>
                  <div style={{
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: '#2d3748'
                  }}>
                    {position.user_full_name}
                  </div>
                </div>

                {/* Score and Percentage */}
                <div style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  background: '#f7fafc',
                  borderRadius: '8px'
                }}>
                  <div style={{
                    fontSize: '0.85rem',
                    color: '#718096',
                    marginBottom: '0.25rem',
                    fontWeight: 600
                  }}>
                    {t('winners.result')}
                  </div>
                  <div style={{
                    fontSize: '1.3rem',
                    fontWeight: 700,
                    color: '#667eea',
                    marginBottom: '0.25rem'
                  }}>
                    {position.total_score.toLocaleString('ru-RU')}
                    <span style={{ 
                      fontSize: '0.9rem', 
                      color: '#718096', 
                      fontWeight: 500,
                      marginLeft: '0.25rem'
                    }}>
                      из {position.max_possible_score.toLocaleString('ru-RU')}
                    </span>
                  </div>
                  <div style={{
                    fontSize: '1rem',
                    color: '#4a5568',
                    fontWeight: 600,
                    marginBottom: '0.5rem'
                  }}>
                    {position.percentage.toFixed(1)}% {t('game.accuracy')}
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#9ca3af',
                    paddingTop: '0.5rem',
                    borderTop: '1px solid #e2e8f0'
                  }}>
                    Минимальный порог {position.threshold}%
                  </div>
                  {position.cascadeNote && (
                    <div style={{
                      fontSize: '0.8rem',
                      color: '#f59e0b',
                      marginTop: '0.75rem',
                      padding: '0.75rem',
                      background: '#fffbeb',
                      borderRadius: '6px',
                      border: '1px solid #fde68a',
                      lineHeight: '1.4'
                    }}>
                      ⚠️ {position.cascadeNote}
                    </div>
                  )}
                </div>

                {/* Date/Time */}
                {position.achieved_at && (
                  <div style={{
                    marginTop: '0.75rem',
                    paddingTop: '0.75rem',
                    borderTop: '1px solid #e2e8f0',
                    fontSize: '0.9rem',
                    color: '#718096',
                    textAlign: 'center'
                  }}>
                    {formatDateTime(position.achieved_at)}
                  </div>
                )}
                
                {/* Share buttons for current user if they are a winner */}
                {currentUserId && position.user_id === currentUserId && (
                  <ShareButtonsCompact 
                    gameId={gameId}
                    userId={position.user_id}
                    gameName={game?.name || ''}
                    accuracy={position.percentage.toFixed(1)}
                    place={position.place}
                    t={t}
                  />
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WinnersPodium;
